import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import api from '../../services/api';
import { useCart } from '../../context/CartContext';
import { getPedidoById, Pedido, getEstadoLabel, getEstadoPagoLabel } from '../../services/pedidos';

interface PaymentInfo {
  id: string;
  status: string;
  status_detail: string;
  amount: number;
  external_reference: string;
}

const formatPrice = (price: number | string): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numPrice);
};

const MercadoPagoResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'success' | 'pending' | 'error' | null>(null);
  const [message, setMessage] = useState('');
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [loadingPedido, setLoadingPedido] = useState(false);

  useEffect(() => {
    const verifyPayment = async () => {
      // Obtener parámetros de la URL que MercadoPago envía
      const paymentStatus = searchParams.get('status') || searchParams.get('collection_status');
      const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');
      const externalReference = searchParams.get('external_reference');

      console.log('🔍 MercadoPago payment result:', { paymentStatus, paymentId, externalReference });

      // Si tenemos payment_id, verificar con el backend
      if (paymentId) {
        try {
          // 1. Obtener información del pago
          const response = await api.get<PaymentInfo>(`/pagos/mercadopago/payment/${paymentId}/`);
          setPaymentInfo(response.data);
          
          console.log('💳 Información del pago:', response.data);
          
          if (response.data.status === 'approved') {
            // 2. Intentar crear el pedido si no existe (para localhost sin webhook)
            try {
              // Recuperar datos del carrito y dirección del localStorage
              const cartDataStr = localStorage.getItem('mercadopago_cart_data');
              const direccionIdStr = localStorage.getItem('mercadopago_direccion_id');
              
              if (cartDataStr && direccionIdStr) {
                const cartData = JSON.parse(cartDataStr);
                const direccionId = parseInt(direccionIdStr);
                
                console.log('🛒 Creando pedido con datos del localStorage:', { cartData, direccionId });
                
                const crearPedidoResponse = await api.post('/pagos/mercadopago/procesar-pago/', {
                  payment_id: paymentId,
                  cart_data: cartData,
                  direccion_id: direccionId
                });
                
                console.log('✅ Respuesta crear pedido:', crearPedidoResponse.data);
                
                if (crearPedidoResponse.data.numero_pedido) {
                  setOrderNumber(crearPedidoResponse.data.numero_pedido);
                  // Cargar datos completos del pedido
                  await loadPedidoData(crearPedidoResponse.data.pedido_id.toString());
                  
                  // Limpiar localStorage
                  localStorage.removeItem('mercadopago_cart_data');
                  localStorage.removeItem('mercadopago_direccion_id');
                }
              } else {
                console.warn('⚠️ No se encontraron datos del carrito en localStorage');
              }
            } catch (createError: any) {
              console.error('Error creando pedido:', createError);
              // Si el pedido ya existe, intentar cargarlo
              if (createError.response?.data?.numero_pedido) {
                setOrderNumber(createError.response.data.numero_pedido);
                await loadPedidoData(createError.response.data.pedido_id.toString());
              }
            }
            
            setStatus('success');
            setMessage('¡Tu pago ha sido procesado exitosamente!');
            // Limpiar el carrito al aprobar el pago
            clearCart();
            
          } else if (response.data.status === 'pending' || response.data.status === 'in_process') {
            setStatus('pending');
            setMessage('Tu pago está siendo procesado. Te notificaremos cuando se confirme.');
          } else {
            setStatus('error');
            setMessage(`El pago fue ${response.data.status_detail || 'rechazado'}. Por favor, intenta nuevamente.`);
          }
        } catch (error) {
          console.error('❌ Error verificando pago:', error);
          // Fallback al status de la URL
          handleStatusFromUrl(paymentStatus);
        }
      } else {
        // Usar el status de la URL
        handleStatusFromUrl(paymentStatus);
      }
      
      setLoading(false);
    };

    const handleStatusFromUrl = (paymentStatus: string | null) => {
      if (paymentStatus === 'approved') {
        setStatus('success');
        setMessage('¡Tu pago ha sido procesado exitosamente!');
        // Limpiar el carrito al aprobar el pago
        clearCart();
      } else if (paymentStatus === 'pending' || paymentStatus === 'in_process') {
        setStatus('pending');
        setMessage('Tu pago está siendo procesado. Te notificaremos cuando se confirme.');
      } else {
        setStatus('error');
        setMessage('El pago no pudo ser procesado. Por favor, intenta nuevamente.');
      }
    };

    const loadPedidoData = async (pedidoId: string) => {
      try {
        setLoadingPedido(true);
        const id = parseInt(pedidoId);
        if (!isNaN(id)) {
          const pedidoData = await getPedidoById(id);
          setPedido(pedidoData);
          console.log('✅ Pedido cargado:', pedidoData);
        }
      } catch (error) {
        console.error('❌ Error al cargar pedido:', error);
      } finally {
        setLoadingPedido(false);
      }
    };

    verifyPayment();
  }, [searchParams, clearCart]);

  const handleContinue = () => {
    if (status === 'success' || status === 'pending') {
      navigate('/mi-cuenta/pedidos');
    } else {
      navigate('/carrito');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verificando pago...
        </Typography>
      </Container>
    );
  }

  const getIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />;
      case 'pending':
        return <HourglassEmptyIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />;
      default:
        return <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return '¡Pago Exitoso!';
      case 'pending':
        return 'Pago Pendiente';
      default:
        return 'Error en el Pago';
    }
  };

  const getSeverity = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      default:
        return 'error';
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
        {/* Encabezado con estado */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {getIcon()}
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {getTitle()}
          </Typography>

          <Alert severity={getSeverity() as 'success' | 'warning' | 'error'} sx={{ mt: 2, borderRadius: '12px' }}>
            {message}
          </Alert>
        </Box>

        {/* Información del pedido */}
        {(status === 'success' || status === 'pending') && pedido && (
          <>
            {/* Número de pedido */}
            <Box sx={{ textAlign: 'center', mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: '15px' }}>
              <Typography variant="body2" color="text.secondary">
                Número de Pedido
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 'bold' }}>
                {pedido.numero_pedido}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Chip 
                  label={getEstadoLabel(pedido.estado)} 
                  color="primary" 
                  variant="outlined"
                />
                <Chip 
                  label={getEstadoPagoLabel(pedido.pagado)} 
                  color={pedido.pagado ? 'success' : 'warning'} 
                  variant="filled"
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Productos comprados */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShoppingBagIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Productos ({pedido.items?.length || 0})
                </Typography>
              </Box>
              
              <Card variant="outlined" sx={{ borderRadius: '12px' }}>
                <List disablePadding>
                  {pedido.items?.map((item, index) => (
                    <React.Fragment key={item.id || index}>
                      <ListItem sx={{ py: 2 }}>
                        <ListItemAvatar>
                          <Avatar 
                            src={item.producto_detalle?.imagen} 
                            variant="rounded"
                            sx={{ width: 56, height: 56, bgcolor: 'grey.100' }}
                          >
                            <ShoppingBagIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography fontWeight="500">
                              {item.producto_nombre || item.producto_detalle?.nombre || `Producto #${item.producto}`}
                            </Typography>
                          }
                          secondary={`Cantidad: ${item.cantidad} × ${formatPrice(item.precio_unitario)}`}
                          sx={{ ml: 1 }}
                        />
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                          {formatPrice(item.precio_total)}
                        </Typography>
                      </ListItem>
                      {index < (pedido.items?.length || 0) - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            </Box>

            {/* Dirección de envío */}
            {pedido.direccion_entrega_detalle && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalShippingIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Envío a
                  </Typography>
                </Box>
                
                <Card variant="outlined" sx={{ borderRadius: '12px', p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <LocationOnIcon color="action" sx={{ mt: 0.5 }} />
                    <Box>
                      <Typography fontWeight="500">
                        {pedido.direccion_entrega_detalle.nombre || 'Dirección de entrega'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {pedido.direccion_entrega_detalle.direccion_linea1}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {pedido.direccion_entrega_detalle.distrito}, {pedido.direccion_entrega_detalle.ciudad}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>
            )}

            {/* Resumen de pago */}
            <Box sx={{ mb: 4 }}>
              <Card sx={{ borderRadius: '12px', bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography color="text.secondary">Subtotal</Typography>
                    <Typography>{formatPrice(pedido.subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography color="text.secondary">Envío</Typography>
                    <Typography color="success.main" fontWeight="500">
                      {parseFloat(pedido.costo_delivery) > 0 ? formatPrice(pedido.costo_delivery) : 'Gratis'}
                    </Typography>
                  </Box>
                  {parseFloat(pedido.descuento) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography color="text.secondary">Descuento</Typography>
                      <Typography color="error.main">-{formatPrice(pedido.descuento)}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">Total Pagado</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {formatPrice(pedido.total)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Información de pago */}
            {paymentInfo && (
              <Box sx={{ mb: 4, p: 2, bgcolor: 'grey.100', borderRadius: '12px' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>ID de Transacción MercadoPago:</strong> {paymentInfo.id}
                </Typography>
              </Box>
            )}

            {/* Mensaje de próximos pasos */}
            <Alert severity="info" sx={{ borderRadius: '12px', mb: 3 }}>
              <Typography variant="body2">
                <strong>¿Qué sigue?</strong> Tu pedido está siendo preparado. Te notificaremos cuando esté listo para envío.
                Puedes seguir el estado de tu pedido desde "Mis Pedidos".
              </Typography>
            </Alert>
          </>
        )}

        {/* Información básica si no hay datos del pedido */}
        {(status === 'success' || status === 'pending') && !pedido && !loadingPedido && paymentInfo && (
          <Box sx={{ mb: 3, textAlign: 'left', bgcolor: 'grey.100', p: 2, borderRadius: '12px' }}>
            <Typography variant="body2"><strong>ID de Pago:</strong> {paymentInfo.id}</Typography>
            {paymentInfo.amount && (
              <Typography variant="body2"><strong>Monto:</strong> {formatPrice(paymentInfo.amount)}</Typography>
            )}
            {orderNumber && (
              <Typography variant="body2"><strong>Número de Pedido:</strong> {orderNumber}</Typography>
            )}
          </Box>
        )}

        {loadingPedido && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Botones de acción */}
        <Box sx={{ mt: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            sx={{
              borderRadius: '12px',
              py: 1.5,
              px: 4,
              background: status === 'error' ? 'linear-gradient(45deg, #f44336, #e53935)' : 'linear-gradient(45deg, #2e5a33, #4a7c59)',
              '&:hover': {
                background: status === 'error' ? 'linear-gradient(45deg, #d32f2f, #c62828)' : 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
              },
            }}
          >
            {status === 'error' ? 'Volver al Carrito' : 'Ver Mis Pedidos'}
          </Button>
          {(status === 'success' || status === 'pending') && (
            <Button
              variant="outlined"
              size="large"
              onClick={handleGoHome}
              sx={{
                borderRadius: '12px',
                py: 1.5,
                px: 4,
                borderColor: '#2e5a33',
                color: '#2e5a33',
                '&:hover': {
                  borderColor: '#1a3d1e',
                  bgcolor: 'rgba(46, 90, 51, 0.04)',
                },
              }}
            >
              Seguir Comprando
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default MercadoPagoResult;
